import { FormApi } from 'final-form'
import { StudioSystemType } from 'types/global'
import { DropdownInput, NativeMountingInputs, TextFieldInput } from 'types/mounting'
import { RoofTypeName } from '../../types'
import { getRoofName } from '../../utils'
import { NOVA_LABELS, NOVA_RAIL_ORIENTATION_OPTIONS } from './novaLabels'

const TILE_ROOF_TYPES: RoofTypeName[] = ['Tile Clay', 'Tile Concrete']

export function isNovaTiltActive(system: StudioSystemType): boolean {
  const tiltOption = String(system?.integration_json?.nova?.tiltOption ?? 'No tilt')
  return tiltOption !== 'No tilt'
}

export function getRailOrientationOptions(isTileRoof: boolean, system: StudioSystemType) {
  const { shortSide, longSide } = NOVA_RAIL_ORIENTATION_OPTIONS
  if (isNovaTiltActive(system)) return [shortSide]
  if (!isTileRoof) return [shortSide, longSide]
  const hasLandscape = system.moduleGrids?.()?.some((mg: any) => mg.moduleLayout() === 'landscape')
  return [hasLandscape ? longSide : shortSide]
}

export function getNovaChannelOptions(system: StudioSystemType): DropdownInput['options'] {
  if (isNovaTiltActive(system)) return [{ label: '2', value: 2 }]
  return [
    { label: '2', value: 2 },
    { label: '3', value: 3 },
  ]
}

export function createNovaInputs(): (projectForm: FormApi, system: StudioSystemType) => NativeMountingInputs {
  return (projectForm: FormApi, system: StudioSystemType): NativeMountingInputs => {
    const roofType = getRoofName(projectForm.getState().values.roof_type)
    const isTileRoof = roofType ? TILE_ROOF_TYPES.includes(roofType) : false
    const currentChannels = Number(system?.integration_json?.nova?.channels) || 2

    const dropdownInputs: DropdownInput[] = []

    if (roofType === 'Kliplock') {
      dropdownInputs.push({
        label: 'Kliplock Bracket',
        variableName: 'kliplokBracket',
        default: 'Mini Kliplok Bracket',
        options: [
          { label: 'Full size Kliplok Bracket', value: 'Full size Kliplok Bracket' },
          { label: 'Mini Kliplok Interface Bracket', value: 'Mini Kliplok Bracket' },
        ],
      })
    }

    if (!isTileRoof && currentChannels !== 3) {
      const hasLandscape = system.moduleGrids?.()?.some((mg: any) => mg.moduleLayout() === 'landscape')

      dropdownInputs.push({
        label: 'Tilt Option',
        variableName: 'tiltOption',
        default: 'No tilt',
        options: hasLandscape
          ? [{ label: 'No tilt', value: 'No tilt' }]
          : [
              { label: 'No tilt', value: 'No tilt' },
              { label: '10-15 deg', value: '10-15 deg' },
              { label: '15-30 deg', value: '15-30 deg' },
              { label: '30-60 deg', value: '30-60 deg' },
              { label: 'A-frame (15-30 deg)', value: 'A-frame (15-30 deg)' },
            ],
      })
    }

    dropdownInputs.push({
      label: 'Number of Rails',
      variableName: 'channels',
      default: 2,
      options: getNovaChannelOptions(system),
    })

    dropdownInputs.push({
      label: 'Rail Length',
      variableName: 'railLength',
      default: 4700,
      options: [
        { label: '4700 mm', value: 4700 },
        { label: '2400 mm', value: 2400 },
      ],
    })

    const railOptions = getRailOrientationOptions(isTileRoof, system)
    dropdownInputs.push({
      label: 'Rail Orientation',
      variableName: 'railOrientation',
      default: railOptions[0].value,
      options: railOptions,
    })

    dropdownInputs.push({
      label: 'Component Colour',
      variableName: 'componentColour',
      default: 'Silver',
      tooltip: NOVA_LABELS.componentColour2400Tooltip,
      options: [
        { label: 'Silver', value: 'Silver' },
        { label: 'Black', value: 'Black' },
      ],
    })

    if (roofType === 'Metal Tin') {
      dropdownInputs.push({
        label: 'Corrugated?',
        variableName: 'corrugatedRoof',
        default: 'No',
        options: [
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
        ],
      })
    }

    const hasStrings = system.inverters?.()?.some((inv: any) => {
      const mppts = inv.mppts?.() ?? []
      return mppts.some((mppt: any) => (mppt.strings?.()?.length ?? 0) > 0)
    })
    if (hasStrings) {
      dropdownInputs.push({
        label: 'Isolator Covers',
        variableName: 'useIsolatorCovers',
        default: 'No',
        options: [
          { label: 'Yes', value: 'Yes' },
          { label: 'No', value: 'No' },
        ],
      })
    }

    const textFieldInputs: TextFieldInput[] = [
      {
        label: 'Foot Spacing (mm)',
        variableName: 'footSpacing',
        default: 1200,
        type: 'number',
        min: 350,
        max: 2000,
      },
      {
        label: 'Extra Length (mm)',
        variableName: 'extraLength',
        default: 0,
        type: 'number',
        min: 0,
        max: 10000,
      },
    ]

    return { dropdownInputs, textFieldInputs }
  }
}
